import { useMainAreaPortalEl } from "@/components/layout/mainAreaPortal.tsx";
import { Dialog, DialogOverlay, DialogPortal } from "@/components/ui/dialog.tsx";
import { useIsMobile } from "@/hooks/use-mobile.tsx";
import { cn } from "@/lib/utils.ts";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { Minimize2, X } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import type { ReactNode } from "react";

/**
 * Shared dialog shell for "open this artifact full-screen" UX. Used by
 * `HeavyArtifactItem` (its inline maximize button) and `ArtifactBiscuit`
 * (the omnibox-overlay summary tap). Owns the responsive sizing, header
 * chrome, and footer slot — leaves the body to the caller so each surface
 * can pick its own renderer (legacy per-type, schema-driven, error-boundary
 * wrapped, etc.).
 */
export const ArtifactLightbox = ({
  open,
  onOpenChange,
  icon: Icon,
  title,
  hideHeader,
  hideFooter,
  footerSlot,
  headerActions,
  children,
}: {
  open: boolean;
  onOpenChange: (next: boolean) => void;
  icon: LucideIcon;
  title: string;
  /** Hide the in-dialog header (used when the native webview supplies its own). */
  hideHeader?: boolean;
  /** Hide the in-dialog footer (used when the native webview supplies action buttons). */
  hideFooter?: boolean;
  /** Footer content. Rendered inside a styled bar at the bottom of the dialog. */
  footerSlot?: ReactNode;
  /** Controls in the header, just before the close button. */
  headerActions?: ReactNode;
  /** Body content — typically `<ArtifactView>` or the legacy `<ArtifactContent>`. */
  children: ReactNode;
}) => {
  const isMobile = useIsMobile();
  const mainAreaEl = useMainAreaPortalEl();

  // On desktop, scope the lightbox to the main content column so it shrinks and
  // re-centers when the right sidebar opens (instead of covering the full
  // viewport and the sidebar). Scoped → non-modal so the sidebar stays
  // interactive beside it. Falls back to the full-viewport modal when there's
  // no main-area element (e.g. outside a ViewWrapper).
  const scoped = !isMobile && !!mainAreaEl;

  // Run non-modal on mobile too (not just the scoped desktop case). Modal Radix
  // wraps its content in `react-remove-scroll`, whose non-passive `touchmove`
  // handler calls `preventDefault()` on horizontal drags over content that
  // can't scroll horizontally. That cancels the browser's native
  // drag-to-extend-selection gesture, so inside the editor a double-tapped word
  // can't be extended to select more (the non-modal desktop path never had
  // this). The fullscreen mobile overlay covers the whole viewport, so it needs
  // neither Radix's background scroll-lock nor its focus trap.
  const nonModal = scoped || isMobile;

  return (
    <Dialog open={open} onOpenChange={onOpenChange} modal={!nonModal}>
      <DialogPortal container={scoped ? mainAreaEl : undefined}>
        {scoped ? (
          // Radix `Dialog.Overlay` renders nothing when `modal={false}`, so the
          // scoped (non-modal) lightbox draws its own dim/blur backdrop.
          // `absolute` confines it to the main column (the sidebar stays
          // un-dimmed and interactive); clicking it closes the lightbox.
          <div
            aria-hidden
            onClick={() => onOpenChange(false)}
            className="absolute inset-0 z-40 bg-black/50 backdrop-blur-xs animate-in fade-in-0"
          />
        ) : (
          // Modal Radix already dismisses on outside pointer-down (one
          // onOpenChange(false)). An onClick here would fire a second one, and
          // since close now calls history.back() that would pop two entries.
          // Renders nothing on mobile (non-modal) — the fullscreen content needs
          // no backdrop, and `Dialog.Overlay` returns null when `modal={false}`.
          <DialogOverlay />
        )}
        <DialogPrimitive.Content
          aria-label={title}
          // Non-modal Radix dismisses on any outside pointer-down/focus, which
          // would close the lightbox the moment the user touches the sidebar
          // (scoped) or a portaled editor popup (mobile fullscreen). Suppress
          // that whenever non-modal — close is via the backdrop, header button,
          // or Escape. (No-op handlers in the modal fallback.)
          onPointerDownOutside={nonModal ? (e) => e.preventDefault() : undefined}
          onInteractOutside={nonModal ? (e) => e.preventDefault() : undefined}
          className={cn(
            "z-50 flex flex-col gap-0 overflow-hidden bg-popover text-popover-foreground shadow-lg",
            scoped
              ? "absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[90%] max-w-[1300px] max-h-[90%] border rounded-xl duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=open]:zoom-in-75 data-[state=closed]:zoom-out-75"
              : isMobile
                ? // Pin to top-left with no translate. Centering via translate
                  // interacts badly with iOS Safari/WebView keyboard handling —
                  // fixed elements "pop" as iOS shifts layout for the focused
                  // input. `h-[100dvh]` keeps the footer above the keyboard/URL bar.
                  // No open animation: the fullscreen content fading in would
                  // reveal the thread behind for a frame — reads as a flash when
                  // the lightbox is restored from the URL on back-navigation.
                  "fixed inset-0 top-0 left-0 translate-x-0 translate-y-0 w-screen h-[100dvh] max-w-none max-h-none rounded-none border-0"
                : "fixed left-[50%] top-[50%] -translate-x-[50%] -translate-y-[50%] w-[90vw] max-w-[1300px] max-h-[90vh] border rounded-xl duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=open]:zoom-in-75 data-[state=closed]:zoom-out-75 data-[state=open]:slide-in-from-bottom-0 data-[state=closed]:slide-out-to-bottom-0",
          )}
        >
          <DialogPrimitive.Title className="sr-only">{title}</DialogPrimitive.Title>
          {!hideHeader ? (
            <div
              className={cn(
                "flex flex-row gap-2 items-center border-b px-4 py-3 font-medium shrink-0",
                // Respect the iOS notch / Dynamic Island on mobile Safari.
                isMobile && "pt-[max(0.75rem,env(safe-area-inset-top))]",
              )}
            >
              <Icon className="w-4 h-4" />
              <div className="text-sm">{title}</div>
              <div className="ml-auto flex items-center gap-2">
                {headerActions}
                <button
                  type="button"
                  className="p-1 hover:bg-muted rounded"
                  onClick={() => onOpenChange(false)}
                  aria-label={isMobile ? "Close artifact" : "Minimize artifact"}
                >
                  {isMobile ? (
                    <X className="w-4 h-4 text-muted-foreground" />
                  ) : (
                    <Minimize2 className="w-4 h-4 text-muted-foreground" />
                  )}
                </button>
              </div>
            </div>
          ) : null}

          <div className="overflow-y-auto flex-1 text-sm font-sans leading-normal">{children}</div>

          {!hideFooter && footerSlot ? (
            <div
              className={cn(
                "flex flex-col-reverse md:flex-row gap-2 md:items-center px-4 py-3 rounded-b-md border-t shrink-0 justify-between",
                // Keep action buttons above the iOS home indicator / Safari bottom bar.
                isMobile && "pb-[max(0.75rem,env(safe-area-inset-bottom))]",
              )}
            >
              {footerSlot}
            </div>
          ) : null}
        </DialogPrimitive.Content>
      </DialogPortal>
    </Dialog>
  );
};

/**
 * Convenience: a footer body that mirrors the legacy `HeavyArtifactItem`
 * footer — date on the left, actions on the right. Callers that don't need
 * the date pass any node in directly.
 */
export const ArtifactLightboxFooter = ({
  leftSlot,
  rightSlot,
}: {
  leftSlot?: ReactNode;
  rightSlot?: ReactNode;
}) => (
  <>
    <div className="flex-1">{leftSlot ?? null}</div>
    {rightSlot ?? null}
  </>
);
